import { Check, X } from "lucide-react";
import { FormEvent, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button, Empty, Field, Modal, PageHeader, Textarea, cn } from "../components/ui";
import { api, errMsg } from "../lib/api";
import { ADMIN_ROLES, useAuth } from "../lib/auth";
import { APPROVAL_TYPE, RTYPE_LABELS, fmtDate, fmtDateTime, fmtIDR } from "../lib/format";

export default function Approvals() {
  const { can } = useAuth();
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState<any[]>([]);
  const [dec, setDec] = useState<any>(null);
  const [note, setNote] = useState("");

  const load = useCallback(() => api.get("/approvals", { params: { status: tab } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), [tab]);
  useEffect(() => { load(); }, [load]);

  const decide = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/approvals/${dec.a.id}/decide`, { decision: dec.decision, note });
      toast.success(dec.decision === "approve" ? "Pengajuan disetujui dan diterapkan" : "Pengajuan ditolak");
      setDec(null); setNote("");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const describe = (a: any) => {
    if (a.type === "void_payment") return `Void kwitansi ${a.receipt_no} sebesar ${fmtIDR(a.amount)} (tgl ${fmtDate(a.payload.date)})`;
    const p = a.payload;
    if (p.rtype === "dispensation") return `Dispensasi: geser jatuh tempo ${p.shift_months} bulan`;
    if (p.rtype === "adjust_installment") return `Penyesuaian cicilan menjadi ${fmtIDR(p.new_installment)}`;
    if (p.rtype === "accelerate") return `Percepatan jadwal: jatuh tempo maju ${p.shift_months} bulan`;
    if (p.settle_date) return `Pelunasan maju pada ${fmtDate(p.settle_date)}`;
    return p.advance_months === "all" ? "Pelunasan maju: seluruh sisa cicilan digabung (pelunasan penuh)" : `Pelunasan maju ${p.advance_months} bulan: ${p.advance_months} cicilan berikutnya digabung satu tagihan`;
  };

  return (
    <div data-testid="approvals-page">
      <PageHeader title="Approval" subtitle="Void transaksi & restrukturisasi diputuskan Admin Operasional / Super Admin" />
      <div className="flex gap-1 mb-4 border-b">
        {["pending", "approved", "rejected"].map((t) => (
          <button key={t} data-testid={`approval-tab-${t}`} onClick={() => setTab(t)} className={cn("px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors", tab === t ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground")}>
            {t === "pending" ? "Menunggu" : t === "approved" ? "Disetujui" : "Ditolak"}
          </button>
        ))}
      </div>
      {rows.length === 0 ? <div className="card-surface"><Empty text="Tidak ada pengajuan." /></div> : (
        <div className="grid gap-3">
          {rows.map((a) => (
            <div key={a.id} className="card-surface rise p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`approval-item-${a.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={cn("rounded px-2 py-0.5 text-xs font-bold text-white", a.type === "void_payment" ? "bg-red-600" : "bg-blue-600")}>{APPROVAL_TYPE[a.type]}</span>
                  {a.payload?.rtype && <span className="text-xs font-semibold text-muted-foreground">{RTYPE_LABELS[a.payload.rtype]}</span>}
                  <Link to={`/pinjaman/${a.loan_id}`} className="font-semibold hover:text-primary">{a.student_name} <span className="num text-xs text-muted-foreground">{a.internal_id}</span></Link>
                </div>
                <p className="text-sm mt-1">{describe(a)}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Alasan: {a.reason} · diajukan {a.requested_by} · {fmtDateTime(a.created_at)}</p>
                {a.decided_by && <p className="text-xs text-muted-foreground">Diputuskan {a.decided_by} · {fmtDateTime(a.decided_at)} {a.decision_note && `· "${a.decision_note}"`}</p>}
              </div>
              {a.status === "pending" && can(ADMIN_ROLES) && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" data-testid={`approval-reject-${a.id}`} onClick={() => setDec({ a, decision: "reject" })}><X className="h-4 w-4" /> Tolak</Button>
                  <Button size="sm" data-testid={`approval-approve-${a.id}`} onClick={() => setDec({ a, decision: "approve" })}><Check className="h-4 w-4" /> Setujui</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <Modal open={!!dec} onClose={() => setDec(null)} title={dec?.decision === "approve" ? "Setujui pengajuan?" : "Tolak pengajuan?"} testId="decision-modal">
        <form onSubmit={decide} className="space-y-3">
          <p className="text-sm text-muted-foreground">{dec && describe(dec.a)}. Keputusan dicatat di audit trail.</p>
          <Field label="Catatan keputusan"><Textarea data-testid="decision-note-input" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setDec(null)}>Batal</Button><Button type="submit" variant={dec?.decision === "approve" ? "primary" : "danger"} data-testid="decision-submit-button">{dec?.decision === "approve" ? "Setujui & Terapkan" : "Tolak"}</Button></div>
        </form>
      </Modal>
    </div>
  );
}
